import * as React from 'react';
import { Badge, Image, makeStyles, shorthands, Subtitle1, Subtitle2, Text, tokens } from '@fluentui/react-components';
import { CompassNorthwestRegular, EditFilled, LocationRegular } from '@fluentui/react-icons';
import type { MessageDescriptor } from 'react-intl';
import { defineMessage, useIntl } from 'react-intl';
import type { Account, AccountConnectionRequest } from '@foundershub/api-client';
import { TextLink } from '@foundershub/components';
import IonEye from '../media/IonEyeDefault.svg';
import { productCategoryLabels, socialImpactLabels } from '../modules/intl/metadata';
import { ContentCard, ContentCardHeader } from './ContentCard';
import { MetadataGrid } from './MetadataGrid';

interface Props {
  account: Account | AccountConnectionRequest;
  me: boolean;
}

export function CompanyInfo({ account, me }: Props) {
  const intl = useIntl();
  const classes = useStyles();

  const countryName = new Intl.DisplayNames([intl.locale], { type: 'region' });

  return (
    <ContentCard>
      <ContentCardHeader
        header={<Subtitle1>{intl.formatMessage(companyInfoTitle)}</Subtitle1>}
        description={
          <span className={classes.iconItem}>
            <Image src={IonEye} />
            <Text>{intl.formatMessage(sharedWithExpert)}</Text>
          </span>
        }
        action={
          me ? (
            <TextLink title="Edit" as="a" href="/settings/company" aria-label={intl.formatMessage(editLabel)}>
              <EditFilled className={classes.editIcon} />
            </TextLink>
          ) : null
        }
      />

      <div className={classes.content}>
        <Text>{account.description}</Text>

        <div className={classes.iconItem}>
          <CompassNorthwestRegular fontSize={24} aria-label={intl.formatMessage(websiteLabel)} />

          {account.website ? (
            <div className={classes.bvdv}>
              <TextLink href={account.website} target="_blank" rel="noreferrer">
                {account.website}
              </TextLink>
            </div>
          ) : (
            <NoneLabel label={noWebsiteLabel} />
          )}
        </div>

        {me ? (
          <div className={classes.iconItem}>
            <LocationRegular fontSize={24} aria-label={intl.formatMessage(addressLabel)} />
            <div className={classes.bvdv}>
              <Text>{`${countryName.of(account.country)}`}</Text>
            </div>
          </div>
        ) : null}

        {!me ? (
          account.account_level === 'one' ? (
            <div>
              <Subtitle2>{intl.formatMessage(levelOneTitleLabel)}</Subtitle2>
              <p>
                <Text>{intl.formatMessage(levelOneDescriptionLabel)}</Text>
              </p>
            </div>
          ) : account.account_level === 'two' ? (
            <div>
              <Subtitle2>{intl.formatMessage(levelTwoTitleLabel)}</Subtitle2>
              <p>
                <Text>{intl.formatMessage(levelTwoDescriptionLabel)}</Text>
              </p>
            </div>
          ) : account.account_level === 'three' ? (
            <div>
              <Subtitle2>{intl.formatMessage(levelThreeTitleLabel)}</Subtitle2>
              <p>
                <Text>{intl.formatMessage(levelThreeDescriptionLabel)}</Text>
              </p>
            </div>
          ) : account.account_level === 'four' ? (
            <div>
              <Subtitle2>{intl.formatMessage(levelFourTitleLabel)}</Subtitle2>
              <p>
                <Text>{intl.formatMessage(levelFourDescriptionLabel)}</Text>
              </p>
            </div>
          ) : null
        ) : null}
        <MetadataGrid
          businessModel={account.business_model ? [account.business_model] : []}
          conceptStage={account.concept_stage ? [account.concept_stage] : []}
          fundingStage={[account.funding_stage]}
          industry={account.industry ? [account.industry] : []}
        />

        <div>
          <Subtitle2>{intl.formatMessage(partnersLabel)}</Subtitle2>
          <Text>
            {account.other_partners.concat(account.partners || []).length ? (
              <div className={classes.chips} role="list" aria-labelledby="partners">
                {account.partners.map((partner, i) => (
                  <Badge appearance="tint" color={me ? 'brand' : 'informative'} size="large" role="listitem" key={i}>
                    {partner}
                  </Badge>
                ))}
                {account.other_partners.map((other_partner, i) => (
                  <Badge appearance="tint" color={me ? 'brand' : 'informative'} size="large" role="listitem" key={i}>
                    {other_partner}
                  </Badge>
                ))}
              </div>
            ) : (
              <NoneLabel />
            )}
          </Text>
        </div>

        <div>
          <Subtitle2>{intl.formatMessage(productCategoriesLabel)}</Subtitle2>
          <Text>
            {(account.product_categories || []).length ? (
              <div className={classes.chips} role="list" aria-labelledby="product_categories">
                {account.product_categories.map((slug) => (
                  <Badge appearance="tint" color={me ? 'brand' : 'informative'} size="large" role="listitem" key={slug}>
                    {intl.formatMessage(productCategoryLabels[slug])}
                  </Badge>
                ))}
              </div>
            ) : (
              <NoneLabel />
            )}
          </Text>
        </div>

        <div>
          <Subtitle2>{intl.formatMessage(socialImpactLabel)}</Subtitle2>
          {(account.social_impacts || []).length ? (
            <div className={classes.chips} role="list" aria-labelledby="social_impact">
              {account.social_impacts.map((slug) => (
                <Badge appearance="tint" color={me ? 'brand' : 'informative'} size="large" role="listitem" key={slug}>
                  {intl.formatMessage(socialImpactLabels[slug])}
                </Badge>
              ))}
            </div>
          ) : (
            <NoneLabel />
          )}
        </div>

        <div>
          <Subtitle2>{intl.formatMessage(linksLabel)}</Subtitle2>
          {account.links.length ? (
            <div role="list" aria-labelledby="links">
              {account.links.map((link, i) => (
                <div role="listitem" key={i}>
                  <TextLink href={link.startsWith('http') ? link : `http://${link}`} target="_blank" rel="noreferrer">
                    {link}
                  </TextLink>
                </div>
              ))}
            </div>
          ) : (
            <NoneLabel />
          )}
        </div>
      </div>
    </ContentCard>
  );
}

const NoneLabel = ({ label = noneLabel }: { label?: MessageDescriptor }) => {
  const intl = useIntl();
  const classes = useStyles();
  return <Text className={classes.noneLabel}>{intl.formatMessage(label)}</Text>;
};

const sharedWithExpert = defineMessage({
  defaultMessage: 'Shared with requested Experts',
  description: 'label for shared with expert',
});

const editLabel = defineMessage({
  defaultMessage: 'Edit',
  description: 'link to edit company information',
});

const noWebsiteLabel = defineMessage({
  defaultMessage: 'No website provided',
  description: 'placeholder for company website when a URL has not been provided',
});

const websiteLabel = defineMessage({
  defaultMessage: 'Website',
  description: 'label for website URL',
});

const addressLabel = defineMessage({
  defaultMessage: 'Address',
  description: 'label for postal address',
});

const levelOneTitleLabel = defineMessage({
  defaultMessage: 'Level 1: Ideate',
  description: 'label for account level title',
});

const levelOneDescriptionLabel = defineMessage({
  defaultMessage:
    'They are refining their idea and learning about the entrepreneurial journey all while exploring how they can build their future product.',
  description: 'label for account level description',
});

const levelTwoTitleLabel = defineMessage({
  defaultMessage: 'Level 2: Develop',
  description: 'label for account level title',
});

const levelTwoDescriptionLabel = defineMessage({
  defaultMessage:
    'They have a strong conviction about the value of their solution and are jumping into the development of their minimum viable product.',
  description: 'label for account level description',
});

const levelThreeTitleLabel = defineMessage({
  defaultMessage: 'Level 3: Grow',
  description: 'label for account level title',
});

const levelThreeDescriptionLabel = defineMessage({
  defaultMessage:
    'They are focused on adding new features to their product as they gain customer traction and market fit.',
  description: 'label for account level description',
});

const levelFourTitleLabel = defineMessage({
  defaultMessage: 'Level 4: Scale',
  description: 'label for account level title',
});

const levelFourDescriptionLabel = defineMessage({
  defaultMessage:
    'They have found product market fit with their solution and are ready to focus on sales acceleration.',
  description: 'label for account level description',
});

const socialImpactLabel = defineMessage({
  defaultMessage: 'Social impact',
  description: 'label for list of UN sustainability goals the company is targetting',
});

const productCategoriesLabel = defineMessage({
  defaultMessage: 'Product categories',
  description: 'label for list of product categories',
});

const partnersLabel = defineMessage({
  defaultMessage: 'Partners',
  description: 'label for list of partners',
});

const linksLabel = defineMessage({
  defaultMessage: 'Additional links',
  description: 'label for list of external links about your business',
});

const noneLabel = defineMessage({
  defaultMessage: 'None',
  description: 'displayed when data is missing / empty state',
});

const companyInfoTitle = defineMessage({
  defaultMessage: 'Company information',
  description: 'title for company information card',
});

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXL),
  },
  iconItem: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  noneLabel: {
    display: 'block',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  bvdv: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  chips: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingVerticalXS),
    flexWrap: 'wrap',
  },
  editIcon: {
    height: '20px',
    width: '20px',
  },
});
